// =============================================================================
// Optim TURPE V2 — l'encart de retours sous le résultat (D31, maquette
// mq-retours, partie 8 du dossier d'ouverture, option B).
//
// Ce que le lecteur doit vivre : une étude qu'il lit sans être interrompu.
// L'encart ne s'affiche jamais de lui-même (décision du 06/10/2026 : « le
// formulaire en fin d'étude s'ouvre avec un bouton ou un lien pour ne pas
// polluer ») ; il s'ouvre par le lien « Donner votre avis sur cette étude ».
// La question du recontact est retirée du formulaire (même date).
//
// Où vont les réponses : la route de support existante
// (`POST /api/metier/v5/support`, type « Feedback »). Elle ne range ni le type
// ni les étiquettes dans une propriété filtrable de la base du support : elle
// les écrit dans le corps du ticket. Le seul champ filtrable est le titre ;
// le sujet commence donc par l'étiquette `retour-produit-optimturpe-v2`, pour
// que la vue dédiée du support filtre « le titre contient l'étiquette ».
//
// La mesure : un événement de suivi qui ne porte que les réponses fermées. Le
// texte libre ne sert jamais aux métriques.
//
// Les questions sont celles de la partie 8, sans retouche.
// =============================================================================

import type { FeedbackData } from "@core/services/support.service"

/** L'étiquette de la vue dédiée du support (partie 8 du dossier). */
export const ETIQUETTE_RETOURS = "retour-produit-optimturpe-v2"

/** Longueur maximale du texte libre transmis. */
export const LONGUEUR_MAX_MANQUE = 2000

export type VarianteRetours = "resultat" | "refus"
export type Clarte = "oui" | "en_partie" | "non"
export type Justesse =
    | "oui"
    | "differe_ancienne_version"
    | "differe_facture"
    | "ne_sait_pas"
export type Intention =
    | "demander_modification"
    | "parler_client"
    | "parler_courtier"
    | "contrat_deja_adapte"
    | "pas_decide"
    | "pas_confiance"
export type RefusJustifie = "oui" | "facture_contraire" | "ne_comprend_pas"

export type ReponsesRetours = {
    clarte: Clarte | null
    justesse: Justesse | null
    intention: Intention | null
    refusJustifie: RefusJustifie | null
    manque: string
}

export function reponsesVides(): ReponsesRetours {
    return {
        clarte: null,
        justesse: null,
        intention: null,
        refusJustifie: null,
        manque: "",
    }
}

/** Les textes affichés, mot pour mot ceux de la maquette mq-retours. */
export const TEXTES_RETOURS = {
    enteteResultat: "Votre avis sur cette étude",
    dureeResultat: "3 questions, moins d'une minute",
    enteteRefus: "Votre avis sur ce refus",
    // Contrat v1.2 §8.6.2 : « Votre avis sur ce refus · 2 questions ».
    dureeRefus: "2 questions",
    enteteEnvoye: "Votre avis sur cette étude",
    clarte: "L'étude est-elle claire pour vous ?",
    justesse: "Le résultat vous paraît-il juste ?",
    intention: "Qu'allez-vous faire de cette étude ?",
    refusJustifie: "Ce refus vous paraît-il justifié ?",
    manque: "Qu'est-ce qui vous a manqué ?",
    facultatif: "(facultatif)",
    manqueAide:
        "N'indiquez ni numéro de compteur ni coordonnées : l'étude est déjà jointe à votre avis.",
    envoyer: "Envoyer mon avis",
    plusTard: "Plus tard",
    // Revue du 19/10/2026 (N9) : la page dit où part l'avis. Vérifié dans le
    // code : `SupportService.sendFeedback`, `POST /api/metier/v5/support`,
    // type « Feedback », un ticket pour l'équipe support. Sans promesse de
    // recontact : la question est retirée du formulaire (décision du
    // 06/10/2026, tenue par le test D31).
    merci: "Merci. Votre avis est transmis à l'équipe support.",
    lienPied: "Donner votre avis sur cette étude",
    // Hors maquette : l'état d'envoi et son échec, qu'il faut bien dire.
    envoiEnCours: "Envoi en cours…",
    echecEnvoi:
        "L'envoi n'a pas abouti. Vos réponses sont conservées : vous pouvez réessayer.",
} as const

/** « envoyé le 12/10/2026 ». */
export function libelleEnvoyeLe(iso: string): string {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return "envoyé"
    return `envoyé le ${d.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Europe/Paris",
    })}`
}

type Option<T extends string> = { valeur: T; libelle: string }

export const OPTIONS_CLARTE: Option<Clarte>[] = [
    { valeur: "oui", libelle: "Oui" },
    { valeur: "en_partie", libelle: "En partie" },
    { valeur: "non", libelle: "Non" },
]
export const OPTIONS_JUSTESSE: Option<Justesse>[] = [
    { valeur: "oui", libelle: "Oui" },
    {
        valeur: "differe_ancienne_version",
        libelle: "Non, il diffère de l'ancienne version",
    },
    { valeur: "differe_facture", libelle: "Non, il diffère de ma facture" },
    { valeur: "ne_sait_pas", libelle: "Je ne sais pas" },
]
export const OPTIONS_INTENTION: Option<Intention>[] = [
    {
        valeur: "demander_modification",
        libelle: "Demander la modification au fournisseur",
    },
    { valeur: "parler_client", libelle: "En parler à mon client" },
    // Contrat v1.2 §8.6.3 (lot 4d).
    {
        valeur: "parler_courtier",
        libelle: "En parler à mon courtier ou à mon bureau d'études",
    },
    {
        valeur: "contrat_deja_adapte",
        libelle: "Rien, le contrat est déjà adapté",
    },
    { valeur: "pas_decide", libelle: "Pas encore décidé" },
    {
        valeur: "pas_confiance",
        libelle: "Rien, je n'ai pas confiance dans le résultat",
    },
]
export const OPTIONS_REFUS: Option<RefusJustifie>[] = [
    { valeur: "oui", libelle: "Oui" },
    {
        valeur: "facture_contraire",
        libelle: "Non, ma facture montre le contraire",
    },
    { valeur: "ne_comprend_pas", libelle: "Je ne comprends pas pourquoi" },
]

function libelle<T extends string>(
    options: Option<T>[],
    valeur: T | null
): string {
    if (valeur === null) return "Sans réponse"
    return options.find((o) => o.valeur === valeur)?.libelle ?? "Sans réponse"
}

/**
 * L'avis peut partir dès que « L'étude est-elle claire pour vous ? » a sa
 * réponse : c'est elle que lit la métrique de satisfaction. Les autres
 * questions restent facultatives.
 */
export function peutEnvoyer(reponses: ReponsesRetours): boolean {
    return reponses.clarte !== null
}

// -----------------------------------------------------------------------------
// Le journal : quels avis sont partis, pour ne pas redemander un avis déjà
// donné sur la même étude. Gardé dans le navigateur, par utilisateur ;
// illisible = vide. Les champs d'un journal plus ancien (apparitions,
// compteurs, recontact) sont ignorés à la lecture.
// -----------------------------------------------------------------------------

export type EnvoiRetours = { le: string }

export type JournalRetours = {
    /** Avis envoyés, par identifiant d'étude. */
    envois: Record<string, EnvoiRetours>
}

/** Au plus autant d'avis gardés dans le journal. */
const TAILLE_MAX_JOURNAL = 300

export function journalVide(): JournalRetours {
    return { envois: {} }
}

export function cleJournalRetours(userId: string): string {
    return `optimturpe-v2:retours:${userId || "anonyme"}`
}

/** Lit un journal stocké ; tout ce qui n'a pas la bonne forme est ignoré. */
export function lireJournal(brut: string | null): JournalRetours {
    if (!brut) return journalVide()
    try {
        const v = JSON.parse(brut) as Partial<JournalRetours>
        const journal = journalVide()
        if (v.envois && typeof v.envois === "object") {
            for (const [id, e] of Object.entries(v.envois)) {
                if (e && typeof e.le === "string") {
                    journal.envois[id] = { le: e.le }
                }
            }
        }
        return journal
    } catch {
        return journalVide()
    }
}

export function noterEnvoi(
    journal: JournalRetours,
    etudeId: string,
    maintenant: Date
): JournalRetours {
    const envois = Object.fromEntries(
        Object.entries({
            ...journal.envois,
            [etudeId]: { le: maintenant.toISOString() },
        }).slice(-TAILLE_MAX_JOURNAL)
    )
    return { ...journal, envois }
}

// -----------------------------------------------------------------------------
// Ce qui part au support, et ce qui part à la mesure.
// -----------------------------------------------------------------------------

export type ContexteRetour = {
    /** Identifiant de l'étude, transmis entier : le support doit la retrouver. */
    etudeId: string
    variante: VarianteRetours
    /** État servi du résultat, ou code du refus servi ; jamais inventé. */
    etat: string | null
}

/**
 * Le sujet commence par l'étiquette : c'est le seul champ que la vue du
 * support peut filtrer tant que la route range le type et les étiquettes dans
 * le corps du ticket.
 */
export function sujetRetour(variante: VarianteRetours): string {
    return `${ETIQUETTE_RETOURS} · ${
        variante === "refus" ? "avis sur un refus" : "avis sur une étude"
    }`
}

/**
 * La charge envoyée à la route de support : l'étude, son issue, les réponses.
 * Ni compteur, ni bâtiment, ni montant : l'étude les porte déjà, et la route
 * joint d'elle-même l'utilisateur et sa société.
 */
export function construireRetour(
    contexte: ContexteRetour,
    reponses: ReponsesRetours
): FeedbackData {
    const manque = reponses.manque.trim().slice(0, LONGUEUR_MAX_MANQUE)
    const description: FeedbackData["description"] & Record<string, string> = {
        text: manque || "Aucun commentaire libre.",
        etiquette: ETIQUETTE_RETOURS,
        etude: contexte.etudeId,
        issue:
            contexte.variante === "refus" ? "étude refusée" : "résultat servi",
        etat: contexte.etat ?? "non servi",
        clarte: libelle(OPTIONS_CLARTE, reponses.clarte),
    }
    if (contexte.variante === "refus") {
        description.refus_justifie = libelle(
            OPTIONS_REFUS,
            reponses.refusJustifie
        )
    } else {
        description.justesse = libelle(OPTIONS_JUSTESSE, reponses.justesse)
        description.intention = libelle(OPTIONS_INTENTION, reponses.intention)
    }
    return {
        subject: sujetRetour(contexte.variante),
        description,
        tags: { etiquette: ETIQUETTE_RETOURS, page: "optimturpe-v2" },
    }
}

export type ReponsesMesurees = {
    issue: VarianteRetours
    clarte: Clarte
    justesse?: Justesse
    intention?: Intention
    refus_justifie?: RefusJustifie
}

/** Les seules réponses fermées : le texte libre ne va jamais à la mesure. */
export function reponsesMesurees(
    variante: VarianteRetours,
    reponses: ReponsesRetours
): ReponsesMesurees | null {
    if (reponses.clarte === null) return null
    const mesure: ReponsesMesurees = {
        issue: variante,
        clarte: reponses.clarte,
    }
    if (variante === "refus") {
        if (reponses.refusJustifie)
            mesure.refus_justifie = reponses.refusJustifie
    } else {
        if (reponses.justesse) mesure.justesse = reponses.justesse
        if (reponses.intention) mesure.intention = reponses.intention
    }
    return mesure
}
